<script>
  import { getContext } from "svelte";
  import cloneDeep from "lodash/cloneDeep";
  import range from "lodash/range";
  import { clickOutside } from "svelte-utilities";

  import ProductSummaryTableType from "./ProductSummaryTableType.svelte";
  import ProductSummaryTableUntyped from "./ProductSummaryTableUntyped.svelte";
  import NewProductModal from "#lib/NewProductModal.svelte";
  import groupItems from "@local/extensions/collections/group-items.js";
  import orderProductItems from "#src/extensions/order-product-items.js";
  import { rfqs } from "#src/stores/rfqs.js";
  import { jobs } from "#src/stores/jobs.js";
  import { quotes } from "#src/stores/quotes.js";
  import { addToSortableList } from "@local/extensions/collections/sortable-list.js";
  import { pricingGroupProps, getSurchargePrices } from "@local/lamina-core";
  import { discontiguousKey } from "#src/extensions/event-keys.js";
  import PriceOverrideInput from "./PriceOverrideInput.svelte";

  export let org;
  export let group;
  export let items;
  export let types;
  export let sent = false;
  export let job;
  export let quote;
  export let recordId;
  export let recordType = "rfq";
  export let itemPrices;

  const productLists = getContext("productLists");
  const supplier = getContext("supplier");
  const categories = getContext("categories");
  const selectedProducts = getContext("selectedProducts");
  const priceEntries = getContext("priceEntries");

  let newProductModal;
  let lastSelectedItem;
  let table;

  $: productGroup = $group?.project_type === "product";
  $: grouped = groupItems(items);
  $: ordered = orderProductItems(grouped, types, productGroup, $categories, $supplier);
  $: counts = getCounts(ordered);
  $: fabrications = sumFabrications(items);
  $: areaNumeric = sumArea(items);
  $: area = `${areaNumeric.toLocaleString("en-US", { maximumFractionDigits: 2 })} sf`;
  $: weightNumeric = sumWeight(items);
  $: weight =
    weightNumeric === 0 ? "" : `${weightNumeric.toLocaleString("en-US", { maximumFractionDigits: 0 })} lbs`;
  $: qty = items.reduce((acc, item) => acc + item.quantity, 0);
  $: jobProperties = pricingGroupProps($group);
  $: shipping = getShipping(job, quote, $supplier, weight);
  $: currentPl = $productLists.find((pl) => pl.id === job?.product_list_id);
  $: surcharges = $group?.surcharges
    ? $group.surcharges
    : presentSurcharges(items, $supplier, currentPl, $priceEntries);
  $: surchargePrices = getSurchargePrices($group, surcharges, currentPl, $priceEntries, itemPrices);
  $: surchargeTotals = totalSurcharges(items, surcharges, surchargePrices);
  $: price = sumPrice($group, items, itemPrices, quote, surcharges, surchargePrices);
  $: totalPrice = computeTotalPrice(quote, sent, price, shipping);
  $: unPricedItems = items.reduce((sum, i) => {
    if (itemPrices?.[i.id] == null) return sum + 1;
    return sum;
  }, 0);

  function totalSurcharges(items, surcharges, surchargePrices) {
    return surcharges.reduce((acc, surcharge) => {
      const surchargeTotal = items.reduce((sum, item) => {
        return sum + (surchargePrices?.[item.id]?.[surcharge.id] || 0);
      }, 0);
      acc[surcharge.id] = surchargeTotal;

      return acc;
    }, {});
  }

  function sumFabrications(items) {
    return items.reduce((sum, item) => {
      if (!item.data.fabrications) return sum;

      const voids = item.data.fabrications.voids?.length || 0;
      const edge = item.data.fabrications.edge?.length || 0;
      const corner = item.data.fabrications.corner?.length || 0;
      const virtual = item.data.fabrications.virtual?.length || 0;

      return sum + voids + edge + corner + virtual;
    }, 0);
  }

  function presentSurcharges(items, supplier, productList, priceEntries) {
    if (!supplier) return [];

    const types = items.reduce((acc, item) => {
      acc[item.type_id] = true;
      return acc;
    }, {});

    const prods = Object.keys(types).reduce((acc, typeId) => {
      const type = $group.types[typeId];
      acc[type?.product_id] = true;
      return acc;
    }, {});

    const surcharges = supplier.products.filter((p) => p.application === "surcharge");

    return surcharges.filter((surcharge) => {
      return (
        surcharge.data?.affected_products?.some((pid) => prods[pid]) &&
        priceEntries[surcharge.id]?.[productList?.id]
      );
    });
  }

  function getShipping(job, quote, $supplier, weight) {
    if (sent && quote?.data?.shipping != null) {
      return quote.data.shipping / 100;
    }

    return null;
  }

  function sumArea(items) {
    return items.reduce((acc, item) => {
      return acc + (item.cache?.area || 0);
    }, 0);
  }

  function sumWeight(items) {
    return items.reduce((acc, item) => {
      return acc + (item.cache?.weight || 0);
    }, 0);
  }

  function quoteItemPrice(quoteItem) {
    if (quoteItem.price_override != null) {
      return quoteItem.price_override;
    }
    return quoteItem.total_price;
  }

  function sumPrice(grp, items, itemPrices, quote, surcharges, surchargePrices) {
    if (sent && quote?.data?.price_subtotal != null) {
      return quote.data.price_subtotal / 100;
    }

    const subtotal = items.reduce((acc, item) => {
      const st = surcharges.reduce((sum, s) => sum + surchargePrices?.[item.id]?.[s.id] || 0, 0);
      return acc + (itemPrices[item.id] || 0) * item.quantity + st;
    }, 0);

    const qip = grp.quote_items.reduce((sum, qi) => sum + quoteItemPrice(qi), 0);
    return subtotal + qip;
  }

  function computeTotalPrice(quote, sent, price, shipping) {
    if (sent && quote?.price) {
      return quote.price / 100;
    }

    return (price || 0) + (shipping || 0);
  }

  function getCounts(ordered) {
    const counts = [0];
    ordered.slice(1).forEach((t, ti) => {
      const current = counts[ti];
      const prev = ordered[ti].items.length;
      counts.push(current + prev);
    });
    return counts;
  }

  function updateItem(e) {
    const { item, ...update } = e.detail;
    group.updateItem(item.id, { ...update });
  }

  function updateQuoteItem({ id, diff }) {
    group.updateQuoteItem(id, diff);
  }

  function deleteItem(e) {
    const { item } = e.detail;
    group.removeItem(item.id);
  }

  function beginAddingProduct() {
    newProductModal.open({ category: null });
  }

  function editItem(e) {
    newProductModal.open({ item: e.detail.id, category: null });
  }

  function refreshList() {
    if (recordType === "rfq") {
      rfqs.fetchOne(recordId);
    } else if (recordType === "quote") {
      quotes.fetchOne(recordId);
    } else if (recordType === "job") {
      jobs.fetchOne(recordId);
    }
  }

  async function addProduct(e) {
    const { item_id, type_id, product, item, fabrications } = e.detail;
    const updates = [];

    if (fabrications?.length) {
      const data = cloneDeep($group.data);
      addToSortableList(data.fabrications, ...fabrications);

      updates.push({
        type: "group",
        id: $group.id,
        prop: "data",
        value: data,
        block: true,
      });
    }

    if (item_id) {
      const i = $group.items[item_id];
      updates.push({
        type: "item",
        id: item_id,
        prop: "quantity",
        value: (i?.quantity || 0) + 1,
      });
      // await group.updateItem(item_id, "quantity", (i?.quantity || 0) + 1);
    } else if (type_id) {
      updates.push({
        type: "item",
        action: "add",
        records: [item],
      });
      // await group.addItem(item);
    } else {
      updates.push(
        { type: "type", action: "add", records: [product], block: true },
        { type: "item", action: "add", records: [item] },
      );
    }

    await group.update(updates);
    refreshList();
  }

  async function updateType(e) {
    const { type_id, diff } = e.detail;
    await group.updateType(type_id, undefined, undefined, diff);
    refreshList();
  }

  async function updateProduct(e) {
    const { type_id, type_diff, item_id, item_diff, fabrications } = e.detail;
    const updates = [];

    if (fabrications?.length) {
      const data = cloneDeep($group.data);
      addToSortableList(data.fabrications, ...fabrications);

      updates.push({
        type: "group",
        id: $group.id,
        prop: "data",
        value: data,
        block: true,
      });
    }

    if (item_id) {
      updates.push({
        type: "item",
        id: item_id,
        diff: item_diff,
      });
    }

    if (type_id) {
      updates.push({
        type: "type",
        id: type_id,
        diff: type_diff,
      });
    }

    await group.update(updates);
    refreshList();
  }

  function getItemList(ordered) {
    return ordered.reduce((acc, t) => {
      return acc.concat(t.items);
    }, []);
  }

  function selectProduct(e) {
    const { item, event } = e.detail;
    // Deselect all quote items
    $group.quote_items?.forEach((qi) => {
      if ($selectedProducts[qi.id]) {
        selectedProducts.deselect(qi.id);
      }
    });

    if (event.shiftKey) {
      document.getSelection()?.removeAllRanges();
      const selectedList = Object.keys($selectedProducts);
      const items = getItemList(ordered);
      if (!selectedList.length) {
        selectedProducts.selectOnly(item.id);
        lastSelectedItem = item.id;
      } else {
        const prev = lastSelectedItem || items[0]?.id;
        const prevIndex = items.findIndex((i) => i.id === prev);
        const currentIndex = items.findIndex((i) => i.id === item.id);
        const min = Math.min(prevIndex, currentIndex);
        const max = Math.max(prevIndex, currentIndex);
        const newSelected = range(min, max + 1).map((i) => items[i].id);
        selectedProducts.select(...newSelected);
        lastSelectedItem = item.id;
      }
    } else if (discontiguousKey(event)) {
      selectedProducts.toggle(item.id);
      lastSelectedItem = item.id;
    } else {
      selectedProducts.selectOnly(item.id);
      lastSelectedItem = item.id;
    }
  }

  function selectQuoteItem(index) {
    const quoteItem = $group.quote_items[index];
    selectedProducts.selectOnly(quoteItem.id);
  }

  function deselect(e) {
    const computer = e.clickTarget.closest(".price-computer");
    const popover = e.clickTarget.closest(".select-input-popover");
    const selected = e.clickTarget.closest(".selected-actions-popover");
    if (!computer && !popover && !selected) {
      $selectedProducts = {};
    }
  }

  function colLabel($org, id, label) {
    return (
      $org?.data.standard_columns?.[id]?.override?.trim() || $org?.data.standard_columns?.[id]?.label || label
    );
  }
</script>

<div class="px-4 text-xs overflow-x-auto">
  <table class="outline-none w-full" use:clickOutside={[table]} bind:this={table} on:outclick={deselect}>
    <tr class="border-b border-t">
      <th>Line</th>
      <th style="min-width: 300px;">Product Info</th>
      <th class="numeric">
        {colLabel($org, "cache.area", "Est. Area")}
      </th>
      <th class="numeric">
        {colLabel($org, "cache.weight", "Est. Wt.")}
      </th>
      {#if fabrications}
        <th class="numeric">Fabs</th>
      {/if}
      <th class="numeric">
        {colLabel($org, "price", "Price (each)")}
      </th>
      <th class="numeric">
        {colLabel($org, "quantity", "Qty")}
      </th>
      {#each surcharges as surcharge}
        <th class="numeric">{surcharge.name}</th>
      {/each}
      <th class="numeric">
        {colLabel($org, "price_total", "Price")}
      </th>
    </tr>
    {#each ordered as t, ti (t.type?.id)}
      {#if t.type}
        <ProductSummaryTableType
          {sent}
          {group}
          {surcharges}
          type={t.type}
          items={t.items}
          typeIndex={counts[ti]}
          index={t.index}
          productList={currentPl}
          {itemPrices}
          {surchargePrices}
          {jobProperties}
          {fabrications}
          {job}
          on:edit-item={editItem}
          on:click-item={selectProduct}
          on:update-item={updateItem} />
      {:else}
        <ProductSummaryTableUntyped
          {sent}
          {group}
          type={t.type}
          items={t.items}
          typeIndex={counts[ti]}
          index={t.index}
          productList={currentPl}
          {itemPrices}
          {jobProperties}
          {fabrications}
          {job}
          on:edit-item={editItem}
          on:click-item={selectProduct}
          on:update-item={updateItem} />
      {/if}
    {/each}
    {#each $group.quote_items as quoteItem, qii}
      <tr
        on:click={() => selectQuoteItem(qii)}
        class="quote-item-row"
        class:selected={$selectedProducts[quoteItem.id]}>
        <td class="align-top">{qii + ordered.length + 1}</td>
        <td colspan="5" class="align-top">
          <div class="px-2 font-bold">
            {quoteItem.description}
          </div>
          {#if quoteItem.details}
            <div class="text-xs px-4 whitespace-pre-wrap">
              {quoteItem.details}
            </div>
          {/if}
        </td>
        {#if fabrications}
          <td></td>
        {/if}
        <td class="text-right flex gap-1 justify-end">
          <PriceOverrideInput
            item={quoteItem}
            computedPrice={quoteItem.total_price}
            disabled={!currentPl?.allow_price_override || sent}
            on:input={(e) =>
              updateQuoteItem({
                id: quoteItem.id,
                diff: { price_override: e.detail.value },
              })} />
        </td>
      </tr>
    {/each}
    {#if !sent}
      <tr>
        <td colspan={fabrications ? 9 + surcharges.length : 8 + surcharges.length} class="text-center p-2">
          <button
            class="h-16 w-full rounded border text-blue-500 border-dashed border-gray-300 hover:bg-gray-100"
            on:click={beginAddingProduct}>
            + Add Product
          </button>
        </td>
      </tr>
    {/if}
    <tr class="border-b border-t">
      <th></th>
      <th></th>
      <th class="numeric">{colLabel($org, "cache.area", "Est. Area")}</th>
      <th class="numeric">{colLabel($org, "cache.weight", "Est. Wt.")}</th>
      {#if fabrications}
        <th class="numeric">Fabs</th>
      {/if}
      <th></th>
      <th class="numeric">{colLabel($org, "quantity", "Qty")}</th>
      {#each surcharges as surcharge}
        <th class="numeric">{surcharge.name}</th>
      {/each}
      <th class="numeric">{colLabel($org, "price_total", "Price")}</th>
    </tr>
    <tr class="totals">
      <td colspan="2">Subtotal</td>
      <td class="numeric">{area}</td>
      <td class="numeric">{weight}</td>
      {#if fabrications}
        <td class="numeric">{fabrications}</td>
      {/if}
      <td class="numeric"></td>
      <td class="numeric">{qty}</td>
      {#each surcharges as surcharge}
        <td class="numeric"
          >{surchargeTotals[surcharge.id]?.toLocaleString("en-US", { style: "currency", currency: "USD" }) ??
            ""}</td>
      {/each}
      <td class="numeric">{price?.toLocaleString("en-US", { style: "currency", currency: "USD" }) ?? ""}</td>
    </tr>
    {#if sent && quote}
      {#if shipping}
        <tr class="totals">
          <td colspan="2">Shipping & Crating</td>
          <td></td>
          <td></td>
          <td></td>
          {#if fabrications}
            <td></td>
          {/if}
          {#each surcharges as surcharge}
            <td></td>
          {/each}
          <td colspan="2" class="numeric">
            {shipping?.toLocaleString("en-US", { style: "currency", currency: "USD" }) ?? "N/A"}
          </td>
        </tr>
      {/if}
      <tr class="totals">
        <td colspan="2">Total</td>
        <td></td>
        <td></td>
        <td></td>
        {#if fabrications}
          <td></td>
        {/if}
        {#each surcharges as surcharge}
          <td></td>
        {/each}
        <td colspan="2" class="numeric">
          {totalPrice?.toLocaleString("en-US", { style: "currency", currency: "USD" }) ?? ""}
        </td>
      </tr>
    {/if}
  </table>
  {#if unPricedItems}
    <div class="text-red-500 font bold">There are {unPricedItems} item(s) without a defined price.</div>
  {/if}
</div>

<NewProductModal
  bind:this={newProductModal}
  {group}
  {items}
  {types}
  on:updateSupplier
  on:addProduct={addProduct}
  on:updateType={updateType}
  on:updateProduct={updateProduct} />

<style lang="scss">
  table {
    min-width: 36rem;
    margin: 0 auto;
  }

  th {
    text-align: left;
    padding: 0.25rem;
    white-space: nowrap;

    &.numeric {
      text-align: right;
    }
  }

  tr.quote-item-row {
    &.selected {
      @apply bg-blue-50;
      outline: 1px solid oklch(62.3% 0.214 259.815);
    }
  }

  .totals td {
    @apply whitespace-nowrap;
    padding: 0rem 0.25rem 0rem 0.25rem;
    font-weight: bold;
    height: 2rem;

    &.numeric {
      text-align: right;
    }
  }
</style>
